<script lang="ts">
  // View & sort sheet (spec §15, components doc §1 `viewSheet`): sort modes, conversation
  // grouping, notification toggle + push state, density.
  import { t } from '$lib/i18n';
  import { store } from '$lib/store';
  import type { Density, SortMode } from '$lib/types';
  import Icon from '../ui/Icon.svelte';
  import Sheet from '../ui/Sheet.svelte';

  const SORTS: [SortMode, 'sortDate' | 'sortUnread' | 'sortStarred' | 'sortSender' | 'sortSubject'][] = [
    ['date', 'sortDate'],
    ['unread', 'sortUnread'],
    ['starred', 'sortStarred'],
    ['sender', 'sortSender'],
    ['subject', 'sortSubject'],
  ];

  const notifyOn = $derived(store.notifyWanted && store.notifyPerm === 'granted');
  const note = $derived.by(() => {
    if (store.notifyPerm === 'unsupported') return t('notifyUnsupported');
    if (store.notifyPerm === 'denied') return t('notifyDenied');
    if (store.needsInstall) return t('pushInstallFirst');
    return t('pushPerDevice');
  });

  function close() {
    store.viewSheet = false;
  }
  async function setSort(m: SortMode) {
    store.setSortMode(m);
    await store.loadListFromCache(store.currentFolder);
    close();
  }
  async function toggleThreads() {
    store.setThreadView(!store.threadView);
    await store.loadListFromCache(store.currentFolder);
  }
  function setDensity(d: Density) {
    store.setDensity(d);
    close();
  }
  async function toggleNotify() {
    if (notifyOn) await store.disableNotifications();
    else await store.enableNotifications();
  }
</script>

<Sheet label={t('viewSort')} onclose={close}>
  {#each SORTS as [mode, key] (mode)}
    <button type="button" class="fbtn big" class:active={store.sortMode === mode} aria-pressed={store.sortMode === mode} onclick={() => void setSort(mode)}>
      <span class="fi">{#if store.sortMode === mode}<Icon name="check" size={20} />{/if}</span><span class="fn">{t(key)}</span>
    </button>
  {/each}
  <div class="sheet-sep"></div>
  <button type="button" class="fbtn big" aria-pressed={store.threadView} onclick={() => void toggleThreads()}>
    <span class="fi">{#if store.threadView}<Icon name="check" size={20} />{/if}</span><span class="fn">{t('threadsToggle')}</span>
  </button>
  <div class="sheet-sep"></div>
  <button type="button" class="fbtn big notify-opt" aria-pressed={notifyOn} onclick={() => void toggleNotify()}>
    <span class="fi">{#if notifyOn}<Icon name="check" size={20} />{/if}</span><span class="fn">{t('notifyToggle')} <small class="note">{note}</small></span>
  </button>
  {#if notifyOn && store.canPush}
    <div class="pushbox" data-on={store.pushOn ? '1' : '0'}>
      <span class="pushstate">
        {#if store.pushOn}
          <Icon name="check" size={16} /> {t('pushOnHere')} <small>{t('pushDevices', { n: store.pushDevices })}</small>
          {#if !store.pushWatching}<small class="warn">{t('pushWatchOff')}</small>{/if}
        {:else}
          <small>{t('pushPerDevice')}</small>
        {/if}
      </span>
      <button type="button" class="pushtest" disabled={store.pushBusy} onclick={() => void store.testPush()}>{t('pushTest')}</button>
    </div>
  {/if}
  <div class="sheet-sep"></div>
  <button type="button" class="fbtn big density-opt" data-d="cozy" aria-pressed={store.density === 'cozy'} onclick={() => setDensity('cozy')}>
    <span class="fi">{#if store.density === 'cozy'}<Icon name="check" size={20} />{/if}</span><span class="fn">{t('densityCozy')}</span>
  </button>
  <button type="button" class="fbtn big density-opt" data-d="compact" aria-pressed={store.density === 'compact'} onclick={() => setDensity('compact')}>
    <span class="fi">{#if store.density === 'compact'}<Icon name="check" size={20} />{/if}</span><span class="fn">{t('densityCompact')}</span>
  </button>
</Sheet>

<style>
  /* @css shell/ViewSheet start */
  .fbtn {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding-block: var(--space-3);
    padding-inline: var(--space-3);
    min-block-size: var(--control-lg);
    border-radius: var(--radius-control);
    color: var(--text-body);
    width: 100%;
    text-align: start;
    transition: background var(--duration-fast) var(--ease-standard)
  }

  .fbtn:hover {
    background: var(--surface-sunken)
  }

  .fbtn.active {
    background: var(--blue-50);
    color: var(--blue-700);
    font-weight: var(--weight-semibold)
  }

  :global(html.tik-dark) .fbtn.active {
    background: #4a7cf62e;
    color: var(--blue-200)
  }

  .fbtn.big {
    padding: var(--space-3) var(--space-4);
    min-block-size: var(--control-lg)
  }

  .fn .note {
    display: block;
    color: var(--text-muted);
    font-size: var(--text-xs);
    line-height: var(--leading-body);
    margin-top: 2px
  }

  .pushbox {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    padding: 0 var(--space-4) var(--space-3);
    flex-wrap: wrap
  }

  .pushstate {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
    flex: 1 1 60%
  }

  .pushstate small {
    color: var(--text-muted);
    font-size: var(--text-xs);
    line-height: var(--leading-body)
  }

  .pushstate small.warn {
    color: var(--danger-500)
  }

  .pushtest {
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    background: none;
    color: inherit;
    font: inherit;
    padding: var(--space-2) var(--space-4);
    min-height: 44px;
    cursor: pointer
  }

  .pushtest:disabled {
    opacity: .5;
    cursor: default
  }

  .sheet-sep {
    height: 1px;
    background: var(--border-subtle);
    margin: var(--space-2) var(--space-3)
  }

  .fi {
    width: var(--control-xs);
    display: grid;
    place-items: center
  }

  .fn {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis
  }

  .fbtn:active {
    background: var(--mail-pressed)
  }
  /* @css shell/ViewSheet end */
</style>
